import React from "react";

const TWITTER_BLUE = "#1DA1F2";

function fmt(n) {
  if (n == null) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

export function TwitterStatsCard({ pubKey, pubName, pubColor, twData, theme: t }) {
  const isConnected = twData?.configured && !twData?.error && !twData?.invalidToken;
  const isInvalidToken = twData?.invalidToken;
  const isUnconfigured = !twData?.configured;

  const followers    = twData?.followers    ?? 0;
  const following    = twData?.following    ?? 0;
  const tweetCount   = twData?.tweetCount   ?? 0;
  const engRate      = twData?.engagementRate ?? 0;
  const handle       = twData?.handle       ?? null;
  const topTweet     = twData?.topTweet     ?? null;
  const stale        = twData?.stale        ?? false;

  return (
    <div style={{
      background: t.surface,
      border: `1px solid ${isConnected ? TWITTER_BLUE + "44" : t.border}`,
      borderRadius: 16,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    }}>
      {/* Header */}
      <div style={{
        padding: "12px 14px 10px",
        background: isConnected ? `${TWITTER_BLUE}10` : t.surface2,
        borderBottom: `1px solid ${isConnected ? TWITTER_BLUE + "22" : t.border}`,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 14 }}>𝕏</span>
            <span style={{ fontSize: 12, fontWeight: 900, color: t.text, fontFamily: "Georgia, serif" }}>
              {pubName}
            </span>
            {stale && (
              <span style={{ fontSize: 8, color: "#f59e0b", fontWeight: 700 }}>cached</span>
            )}
          </div>
          {isConnected
            ? <span style={{ fontSize: 9, color: "#22c55e", background: "#22c55e11", padding: "3px 8px", borderRadius: 20, border: "1px solid #22c55e33", fontWeight: 700 }}>Live</span>
            : isInvalidToken
              ? <span style={{ fontSize: 9, color: "#DC2626", background: "#DC262611", padding: "3px 8px", borderRadius: 20, border: "1px solid #DC262633", fontWeight: 700 }}>Bad Token</span>
              : <span style={{ fontSize: 9, color: t.textFaint, background: t.surface2, padding: "3px 8px", borderRadius: 20, border: `1px solid ${t.border}` }}>Not Connected</span>
          }
        </div>

        {isConnected
          ? (
            <div>
              <div style={{ fontSize: 22, fontWeight: 900, color: TWITTER_BLUE, fontFamily: "Georgia, serif", lineHeight: 1 }}>
                {fmt(followers)}
              </div>
              <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginTop: 2 }}>
                Followers {handle && <span style={{ color: t.textFaint, textTransform: "none", letterSpacing: 0 }}>· {handle}</span>}
              </div>
            </div>
          )
          : (
            <div style={{ fontSize: 12, color: t.textFaint, fontStyle: "italic" }}>
              {isInvalidToken
                ? "Invalid Bearer Token — please update credentials"
                : isUnconfigured
                  ? "Add Twitter credentials to server/.env"
                  : twData?.error || "Not available"
              }
            </div>
          )
        }
      </div>

      {/* Stats row */}
      {isConnected && (
        <div style={{ padding: "10px 14px", borderBottom: `1px solid ${t.border}`, display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
          {[
            { label: "Following",    value: fmt(following)  },
            { label: "Tweets",       value: fmt(tweetCount) },
            { label: "Eng. Rate",    value: engRate > 0 ? `${engRate}%` : "—" },
          ].map(({ label, value }) => (
            <div key={label} style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 900, color: TWITTER_BLUE }}>{value}</div>
              <div style={{ fontSize: 8, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.6, marginTop: 1 }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Top tweet */}
      {isConnected && topTweet && (
        <div style={{ padding: "10px 14px", flex: 1 }}>
          <div style={{ fontSize: 9, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 5, fontWeight: 700 }}>Top Tweet</div>
          <div style={{
            fontSize: 11, color: t.text, lineHeight: 1.4, marginBottom: 6,
            display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {topTweet.text}
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {topTweet.likes    > 0 && <span style={{ fontSize: 9, color: t.textMuted }}>❤️ {topTweet.likes.toLocaleString()}</span>}
            {topTweet.retweets > 0 && <span style={{ fontSize: 9, color: t.textMuted }}>🔁 {topTweet.retweets.toLocaleString()}</span>}
            {topTweet.replies  > 0 && <span style={{ fontSize: 9, color: t.textMuted }}>💬 {topTweet.replies.toLocaleString()}</span>}
          </div>
        </div>
      )}

      {isConnected && !topTweet && (
        <div style={{ padding: "10px 14px", flex: 1 }}>
          <div style={{ fontSize: 10, color: t.textFaint, fontStyle: "italic" }}>No recent tweets</div>
        </div>
      )}

      {!isConnected && (
        <div style={{ padding: "10px 14px", flex: 1 }}>
          <div style={{ fontSize: 10, color: t.textFaint, fontStyle: "italic" }}>Twitter/X data unavailable</div>
        </div>
      )}
    </div>
  );
}
